// ============================================================================
// Beacon — max-profit-price solver. What is the HIGHEST purchase price at which
// this deal still clears your return targets? Binary-searches the purchase price
// against the deterministic engine so it works no matter how the metrics move.
// This is the ceiling half of the offer: you never bid above the number that
// still makes you money, no matter how motivated the seller looks.
// ============================================================================

import type { Deal } from "../types";
import { calc } from "../engine/engine";

export interface MaxPriceTargets {
  minCap: number; // cap-on-cost floor (fraction, e.g. 0.065)
  minDscr: number; // DSCR floor (e.g. 1.20)
}

export interface MaxPriceResult {
  price: number; // max purchase price meeting the targets (0 if infeasible)
  feasible: boolean; // false when no price clears the targets (e.g. a DSCR wall)
  binding: "cap" | "dscr"; // which target is the active constraint at the ceiling
  capAt: number; // cap-on-cost at the ceiling price
  dscrAt: number; // DSCR at the ceiling price
}

const EPS = 1e-9;

function bindingOf(deal: Deal, price: number, t: MaxPriceTargets): "cap" | "dscr" {
  const m = calc({ ...deal, purchase: Math.max(0, price) });
  // the active constraint is whichever has the least slack above its floor
  return (m.capOnCost - t.minCap) <= (m.dscr - t.minDscr) ? "cap" : "dscr";
}

/** Highest purchase price at which the deal still clears minCap AND minDscr. */
export function maxPriceForReturn(deal: Deal, t: MaxPriceTargets): MaxPriceResult {
  const meets = (p: number): boolean => {
    const m = calc({ ...deal, purchase: Math.max(0, p) });
    return m.capOnCost >= t.minCap - EPS && m.dscr >= t.minDscr - EPS;
  };

  // Infeasible: even a near-free purchase can't clear the targets (a refi/DSCR wall).
  if (!meets(1000)) {
    const m = calc({ ...deal, purchase: 1000 });
    return { price: 0, feasible: false, binding: bindingOf(deal, 1000, t), capAt: m.capOnCost, dscrAt: m.dscr };
  }

  // Expand an upper bound until the targets fail.
  let lo = 1000;
  let hi = Math.max(deal.purchase, deal.rehab + deal.soft, 200_000);
  let guard = 0;
  while (meets(hi) && guard++ < 80) hi *= 1.6;
  if (meets(hi)) {
    // Targets never bind in a sane range — cap at the last tested price.
    const m = calc({ ...deal, purchase: hi });
    return { price: Math.round(hi), feasible: true, binding: bindingOf(deal, hi, t), capAt: m.capOnCost, dscrAt: m.dscr };
  }

  // Binary-search the boundary between meets(lo) and !meets(hi).
  for (let i = 0; i < 60; i++) {
    const mid = (lo + hi) / 2;
    if (meets(mid)) lo = mid; else hi = mid;
  }
  const price = Math.floor(lo);
  const m = calc({ ...deal, purchase: price });
  return { price, feasible: true, binding: bindingOf(deal, price, t), capAt: m.capOnCost, dscrAt: m.dscr };
}
